import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getVMs, deleteVM } from './api';
import { getStatusBadgeClass } from './utils';
import CreateVMModal from './CreateVMModal';
import DeleteConfirmModal from './DeleteConfirmModal';

function VMCard({ vm, onDeleteClick, deleteMutation }) {
  return (
    <div className="card">
      <div className="card-title">
        <span>💻</span>
        <span>{vm.name}</span>
      </div>
      <div className="card-content">
        <div className="info-row"><span className="info-label">Status</span><span className={`badge ${getStatusBadgeClass(vm.status)}`}>{vm.status}</span></div>
        <div className="info-row"><span className="info-label">IP Address</span><span className="info-value"><code>{vm.ip}</code></span></div>
        <div className="info-row"><span className="info-label">Node</span><span className="info-value">{vm.node}</span></div>
        <div className="info-row"><span className="info-label">Resources</span><span className="info-value">{vm.cpu} CPU • {vm.memory}</span></div>
        <div className="info-row"><span className="info-label">Image</span><span className="info-value" style={{ fontSize: '0.75rem', wordBreak: 'break-all' }}>{vm.image}</span></div>
        <div className="info-row"><span className="info-label">Launched</span><span className="info-value">{vm.deployDate ? new Date(vm.deployDate).toLocaleString() : 'N/A'}</span></div>
        <div className="info-row"><span className="info-label">Uptime</span><span className="info-value">{vm.uptime}</span></div>
        <div className="info-row">
          <span className="info-label">Ports</span>
          <div className="port-info">
            <div className="port-item"><span className="port-item-label">SSH</span><span className="port-item-value">{vm.ssh}</span></div>
            <div className="port-item"><span className="port-item-label">VNC</span><span className="port-item-value">{vm.vnc}</span></div>
            <div className="port-item"><span className="port-item-label">Screen</span><span className="port-item-value">{vm.screenshare}</span></div>
          </div>
        </div>
      </div>
      <div className="card-actions">
        <button className="btn btn-danger btn-small" onClick={() => onDeleteClick(vm)} disabled={deleteMutation.isPending}>Delete</button>
      </div>
    </div>
  );
}

function VMsView() {
  const queryClient = useQueryClient();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteVMModal, setShowDeleteVMModal] = useState(false);
  const [selectedVM, setSelectedVM] = useState(null);

  const { data: vmData, isLoading: vmsLoading, error: vmsError, refetch: refetchVMs } = useQuery({
    queryKey: ['vms'],
    queryFn: async () => (await getVMs()).data,
    refetchInterval: 30000,
  });

  const deleteMutation = useMutation({
    mutationFn: deleteVM,
    onSuccess: () => {
      queryClient.invalidateQueries(['vms']);
      queryClient.invalidateQueries(['nodes']);
      setShowDeleteVMModal(false);
      setSelectedVM(null);
    },
  });

  const vms = vmData?.items || [];

  return (
    <div className="section">
      <div className="section-header">
        <h2>
          Virtual Machines{' '}
          {vms.length > 0 && <span style={{ fontSize: '0.875rem', color: '#64748b', fontWeight: 400 }}>({vms.length} total)</span>}
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary" onClick={() => refetchVMs()}>Refresh</button>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)}>+ Create Orka VM</button>
        </div>
      </div>

      {vmsLoading && <div className="loading">Loading Orka VMs...</div>}
      {vmsError && <div className="error"><strong>Error loading VMs:</strong> {vmsError.message}</div>}
      {!vmsLoading && !vmsError && vms.length === 0 && (
        <div className="empty-state">
          <div className="empty-state-icon">💻</div>
          <p>No Orka VMs found</p>
          <button className="btn btn-primary" onClick={() => setShowCreateModal(true)} style={{ marginTop: '1rem' }}>Create Your First VM</button>
        </div>
      )}
      {!vmsLoading && vms.length > 0 && (
        <div className="card-grid">
          {vms.map((vm) => (
            <VMCard
              key={vm.name}
              vm={vm}
              onDeleteClick={(vm) => { setSelectedVM(vm); setShowDeleteVMModal(true); }}
              deleteMutation={deleteMutation}
            />
          ))}
        </div>
      )}

      {/* Modals */}
      {showCreateModal && (
        <CreateVMModal
          onClose={() => setShowCreateModal(false)}
          onSuccess={() => { setShowCreateModal(false); queryClient.invalidateQueries(['vms']); queryClient.invalidateQueries(['nodes']); }}
        />
      )}

      {showDeleteVMModal && selectedVM && (
        <DeleteConfirmModal
          title="Delete VM"
          message={`Are you sure you want to delete VM "${selectedVM.name}"? This action cannot be undone.`}
          onConfirm={() => deleteMutation.mutate(selectedVM.name)}
          onCancel={() => { setShowDeleteVMModal(false); setSelectedVM(null); }}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
}

export default VMsView;
